import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ListChecks, Plus, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { useDefectSubtasks, type DefectSubtask } from "@/hooks/useDefectSubtasks";
import { DONE_STATUSES, type SubtaskProgress } from "@/lib/defectAutomation";

interface DefectSubtasksPanelProps {
  defectId: string;
  teamId: string;
}

/** Prozent-Fortschritt (0–100), 0 wenn keine Unteraufgaben. */
function progressPct(progress: SubtaskProgress): number {
  return progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
}

/**
 * MGM-18 (TES-481) — "Unteraufgaben"-Panel im Defect-Detail.
 *
 * Listet die Unteraufgaben (defects mit parent_defect_id), zeigt einen "x/y"-Fortschritt
 * und erlaubt Hinzufuegen (Titel + optional Faelligkeit) sowie Erledigt-Toggle. User-JWT-
 * scoped (RLS greift). Schreibrechte folgen der defects-Write-RLS (mangelbearbeiter/QM/admin).
 */
export function DefectSubtasksPanel({ defectId, teamId }: DefectSubtasksPanelProps) {
  const { t } = useTranslation();
  const { subtasks, progress, isLoading, error, addSubtask, setSubtaskStatus, isMutating } =
    useDefectSubtasks(defectId, teamId);

  const toggleDone = useCallback(
    (subtask: DefectSubtask, done: boolean) => {
      setSubtaskStatus({ subtaskId: subtask.id, status: done ? "resolved" : "open" });
    },
    [setSubtaskStatus],
  );

  return (
    <section
      data-testid="defect-subtasks-panel"
      className="bg-card border border-outline-variant rounded-lg"
    >
      <div className="p-4 border-b border-outline-variant flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ListChecks className="h-5 w-5 text-primary" aria-hidden="true" />
          <h3 className="font-display text-lg font-semibold">{t("defects.subtasks.title")}</h3>
        </div>
        {progress.total > 0 && (
          <span
            data-testid="subtask-progress-label"
            className="text-sm font-bold text-primary tabular-nums"
          >
            {t("defects.subtasks.progress", { done: progress.done, total: progress.total })}
          </span>
        )}
      </div>

      <div className="p-4 space-y-4">
        {progress.total > 0 && (
          <Progress
            value={progressPct(progress)}
            aria-label={t("defects.subtasks.progressAria", { pct: progressPct(progress) })}
          />
        )}

        <SubtaskListBody
          subtasks={subtasks}
          isLoading={isLoading}
          error={error}
          isMutating={isMutating}
          onToggle={toggleDone}
        />

        <SubtaskAddForm isMutating={isMutating} onAdd={addSubtask} />
      </div>
    </section>
  );
}

/** Lade-/Fehler-/Leer-/Listen-Zustand der Unteraufgaben (haelt die Verzweigung aus dem Panel). */
function SubtaskListBody({
  subtasks,
  isLoading,
  error,
  isMutating,
  onToggle,
}: {
  subtasks: DefectSubtask[];
  isLoading: boolean;
  error: Error | null;
  isMutating: boolean;
  onToggle: (subtask: DefectSubtask, done: boolean) => void;
}) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" aria-hidden="true" />
        {t("common.loading")}
      </div>
    );
  }
  if (error) {
    return (
      <p className="text-sm text-destructive">
        {t("common.error")}: {error.message}
      </p>
    );
  }
  if (subtasks.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">
        {t("defects.subtasks.empty")}
      </p>
    );
  }
  return (
    <ul className="space-y-2" data-testid="subtask-list">
      {subtasks.map((s) => (
        <SubtaskListItem key={s.id} subtask={s} isMutating={isMutating} onToggle={onToggle} />
      ))}
    </ul>
  );
}

/** Eine Unteraufgaben-Zeile (Checkbox + Titel + optionale Faelligkeit). */
function SubtaskListItem({
  subtask,
  isMutating,
  onToggle,
}: {
  subtask: DefectSubtask;
  isMutating: boolean;
  onToggle: (subtask: DefectSubtask, done: boolean) => void;
}) {
  const { t } = useTranslation();
  const done = DONE_STATUSES.has(subtask.status);
  return (
    <li className="flex items-center gap-3 rounded border border-outline-variant px-3 py-2">
      <Checkbox
        checked={done}
        disabled={isMutating}
        onCheckedChange={(checked) => onToggle(subtask, checked === true)}
        aria-label={t("defects.subtasks.toggleAria", { title: subtask.title })}
        data-testid={`subtask-checkbox-${subtask.id}`}
      />
      <span
        className={`flex-1 text-sm break-words ${done ? "line-through text-muted-foreground" : ""}`}
      >
        {subtask.title}
      </span>
      {subtask.due_date && (
        <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
          {new Date(subtask.due_date).toLocaleDateString()}
        </span>
      )}
    </li>
  );
}

/** Add-Formular (Titel + optionale Faelligkeit). Eigene State, ruft onAdd nach Validierung. */
function SubtaskAddForm({
  isMutating,
  onAdd,
}: {
  isMutating: boolean;
  onAdd: (input: { title: string; dueDate?: string | null }) => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");

  const handleAdd = useCallback(() => {
    const trimmed = title.trim();
    if (!trimmed) return;
    onAdd({ title: trimmed, dueDate: dueDate || null });
    setTitle("");
    setDueDate("");
  }, [title, dueDate, onAdd]);

  return (
    <div className="border-t border-outline-variant pt-4 space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="subtask-title">{t("defects.subtasks.newTitleLabel")}</Label>
        <Input
          id="subtask-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t("defects.subtasks.newTitlePlaceholder")}
          maxLength={200}
          data-testid="subtask-title-input"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
        />
      </div>
      <div className="flex items-end gap-2">
        <div className="space-y-1.5 flex-1">
          <Label htmlFor="subtask-due">{t("defects.subtasks.newDueLabel")}</Label>
          <Input
            id="subtask-due"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            data-testid="subtask-due-input"
          />
        </div>
        <Button
          type="button"
          onClick={handleAdd}
          disabled={isMutating || title.trim().length === 0}
          data-testid="subtask-add-button"
        >
          <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
          {t("defects.subtasks.add")}
        </Button>
      </div>
    </div>
  );
}
